import {
  HStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { Button, IconButton, Tag, Tooltip, TruncatableText } from 'Atoms';
import { useRef } from 'react';
import { Typography } from 'Tokens';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { RemoveIcon } from 'Tokens/Icons/Function';
import { useOnClickOutside } from 'usehooks-ts';
import { useTranslation } from 'utils/translation';

interface FullBusinessUnitDetails {
  id: string;
  createdAt: string;
  name: string;
  companyId: string;
  description?: string | null;
  contactPerson: {
    id: string;
    email?: string | null;
    avatarUrl: string;
    displayName: string;
  };
}

export interface FilteredBusinessUnitDisplayItem {
  id: string;
  businessUnit?: FullBusinessUnitDetails | null;
  group?: {
    name: string;
  };
}

const OverflowTagsPopover = ({
  overflowBusinessUnits,
  onRemove,
}: {
  overflowBusinessUnits: FilteredBusinessUnitDisplayItem[];
  onRemove: (id: string) => void;
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const ref = useRef<HTMLElement>(null);

  useOnClickOutside<HTMLElement>(ref as React.RefObject<HTMLElement>, onClose);

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Tag
          padding="8px 16px"
          borderWidth="1px"
          borderColor="border.default"
          bg="bg.default"
          cursor="pointer"
          onClick={onToggle}
          _hover={{ bg: 'bg.hover' }}
          rightIcon={<ChevronDownIcon />}
        >
          <Typography variant="bodyStrong">+{overflowBusinessUnits.length}</Typography>
        </Tag>
      </PopoverTrigger>
      <PopoverContent
        as={VStack}
        spacing="8px"
        width="max-content"
        padding="12px 8px"
        ref={ref}
        maxHeight="300px"
        overflow="auto"
      >
        <VStack width="100%" spacing="4px" align="stretch">
          {overflowBusinessUnits.map((bu) => (
            <HStack
              key={bu.id}
              justifyContent="space-between"
              padding="8px"
              borderRadius="4px"
              _hover={{ bg: 'bg.hover' }}
            >
              <TruncatableText
                w={'200px'}
                text={bu?.businessUnit?.name ?? ''}
                variant="bodyStrong"
                noOfLines={1}
              />
              <IconButton
                aria-label="remove-unit"
                variant="ghost"
                size="xs"
                icon={<RemoveIcon color="inherit" />}
                onClick={() => onRemove(bu.businessUnit?.id ?? '')}
                flexShrink={0}
              />
            </HStack>
          ))}
        </VStack>
      </PopoverContent>
    </Popover>
  );
};

export const FilteredBusinessUnits = ({
  selectedBusinessUnits,
  onSelectionChange,
  maxVisibleTags = 3,
}: {
  selectedBusinessUnits: FilteredBusinessUnitDisplayItem[];
  onSelectionChange: (businessUnitIds: string[]) => void;
  maxVisibleTags?: number;
}) => {
  const { t } = useTranslation('common');

  const visibleBusinessUnits = selectedBusinessUnits.slice(0, maxVisibleTags);
  const overflowBusinessUnits = selectedBusinessUnits.slice(maxVisibleTags);
  const hasOverflow = overflowBusinessUnits.length > 0;

  const handleRemove = (businessUnitId: string) => {
    onSelectionChange(
      selectedBusinessUnits
        .filter((b) => b.businessUnit?.id !== businessUnitId)
        .map((b) => b.businessUnit?.id ?? '')
        .filter(Boolean)
    );
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  return (
    <HStack flexWrap="wrap" spacing="8px">
      {visibleBusinessUnits.map((bu) => (
        <Tooltip key={bu.id} label={bu.businessUnit?.name}>
          <Tag
            padding="8px 16px"
            borderWidth="1px"
            borderColor="border.default"
            bg="bg.default"
            maxWidth="200px"
            aria-label={bu.businessUnit?.name}
          >
            <HStack justifyContent="space-between" width="100%">
              <Typography variant="bodyStrong" noOfLines={1}>
                {bu?.businessUnit?.name}
              </Typography>
              <IconButton
                aria-label="remove-filter"
                variant="ghost"
                size="xs"
                icon={<RemoveIcon color="inherit" />}
                onClick={() => handleRemove(bu.businessUnit?.id ?? '')}
                flexShrink={0}
              />
            </HStack>
          </Tag>
        </Tooltip>
      ))}

      {hasOverflow && (
        <OverflowTagsPopover
          overflowBusinessUnits={overflowBusinessUnits}
          onRemove={handleRemove}
        />
      )}

      <Button
        variant="ghost"
        size="md"
        leftIcon={<RemoveIcon color="inherit" />}
        onClick={handleClearAll}
        flexShrink={0}
      >
        {t('common:button.clearAll')}
      </Button>
    </HStack>
  );
};
